import React, { memo, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import Spinner from '@atlaskit/spinner';
import EmptyState from '@atlaskit/empty-state';
import { CustomPanel, errorNotificationBuilder, NotificationTypes, useNotification } from '@css-xp/base';
import WorldIcon from '@atlaskit/icon/glyph/world';
import Button from '@atlaskit/button/new';
import { Box, Text } from '@atlaskit/primitives';
import {
  FetchCloudSitesForOrganizationQuery,
  FetchCloudSitesForOrganizationQueryVariables,
  InvoiceGroup,
  FilterOption,
} from '@css-xp/graphtypes/src/generated-types-new';
import { renderAccordionSectionLoader } from '@css-xp/ppc-page-loaders';
import {
  emptyStateProp,
  emptyStatePropForWrongSearch,
  failureMessage,
  genericEmptyStateProp,
} from '@css-xp/ppc-common';
import { fetchCloudSitesForOrganization } from '../../queries/fetch-entitlement-cloud-site-ids';
import ProductsTableGroupedUnderSite from './products-table-grouped-under-site';
import useFetchCloudSiteGroupedProducts, { isEmpty } from '../../hooks/use-fetch-cloud-site-grouped-products';
import './sites-table.scss';

interface Props {
  organisationId: string;
  txaId: string;
  invoiceGroupDetails: InvoiceGroup[];
  invoiceGroupLoading: boolean;
  searchedInput?: string;
  subscriptionColumnVersion?: number;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  version?: number;
  filterOptions?: FilterOption[];
  areProductsEmpty?: boolean;
  isMultiInstanceAppsEnabled?: boolean;
}

const SitesTable = ({
  organisationId,
  txaId,
  invoiceGroupDetails,
  invoiceGroupLoading,
  searchedInput,
  subscriptionColumnVersion,
  isSandboxSupportForSubscriptionsEnabled = false,
  version,
  filterOptions,
  areProductsEmpty,
  isMultiInstanceAppsEnabled,
}: Props) => {
  const [selectedCloudSiteIds, setSelectedCloudSiteIds] = useState<string[]>([]);
  const [cloudsiteIdAndProductsMap, setCloudsiteIdAndProductsMap] = useState<Record<string, any>>({});
  const [isFetchAllCloudsiteProductLoading, setIsFetchAllCloudsiteProductLoading] = useState<boolean>(false);
  const [isCollapseAll, setIsCollapseAll] = useState<boolean>(false);
  const [fetchCloudSiteProductsError, setFetchCloudSiteProductsError] = useState<boolean>(false);

  const { getCloudsiteGroupedProducts } = useFetchCloudSiteGroupedProducts(filterOptions);
  const { setNotificationSent, sendErrorNotification } = useNotification();

  const { data, loading, error } = useQuery<
    FetchCloudSitesForOrganizationQuery,
    FetchCloudSitesForOrganizationQueryVariables
  >(fetchCloudSitesForOrganization, {
    variables: {
      id: organisationId,
      transactionAccountId: txaId,
    },
    skip: !organisationId,
    nextFetchPolicy: 'cache-and-network',
  });

  const cloudSites = useMemo(() => data?.cloudSitesForOrganization ?? [], [data]);

  /**
   * This function fetches all the products for the given cloudsiteIds.
   * It create a map of cloudsiteId and its products.
   *
   * This is done for below reason:
   * When user searches for product name or Ent#,
   * We need to show only those cloudsite which is having the searched product.
   * Hence, we need to fetch all the products for all the cloudsiteIds and
   * then filter the cloudsiteIds which is not having any product related to searched Input.
   */

  const fetchAllCloudsiteGroupedProducts = async () => {
    setIsFetchAllCloudsiteProductLoading(true);
    const newCloudsiteIdAndProductsMap: Record<string, any> = {};
    for (const node of cloudSites) {
      if (node && node.cloudSiteId) {
        try {
          const result = await getCloudsiteGroupedProducts(node, txaId, searchedInput);
          newCloudsiteIdAndProductsMap[node.cloudSiteId] = {
            groupedProductDataError: result.groupedProductDataError,
            groupedProductDataLoading: result.groupedProductDataLoading,
            groupedProductData: result.groupedProductData,
          };
        } catch {
          setIsFetchAllCloudsiteProductLoading(false);
          setFetchCloudSiteProductsError(true);
        }
      }
    }
    setCloudsiteIdAndProductsMap(newCloudsiteIdAndProductsMap);
    setIsFetchAllCloudsiteProductLoading(false);
  };

  useEffect(() => {
    if (version === 3 && error) {
      const notificationOptions = errorNotificationBuilder()
        .withId('fetch-cloud-sites-error')
        .withHeader('An error occurred while fetching cloud sites')
        .withMessage(failureMessage())
        .withDismissAfter(5000)
        .isVersion(NotificationTypes.FLAG)
        .build();
      setNotificationSent(true);
      sendErrorNotification(notificationOptions);
    }
  }, [error]);

  useEffect(() => {
    //Using version 3 here as the related changes were already implemented with version 3 check.
    if (version === 3 && fetchCloudSiteProductsError) {
      const notificationOptions = errorNotificationBuilder()
        .withId('fetch-cloud-site-products-error')
        .withHeader('An error occurred while fetching products')
        .withMessage(failureMessage())
        .withDismissAfter(5000)
        .isVersion(NotificationTypes.FLAG)
        .build();
      setNotificationSent(true);
      sendErrorNotification(notificationOptions);
    }
  }, [fetchCloudSiteProductsError]);

  useEffect(() => {
    if (cloudSites.length > 0) {
      fetchAllCloudsiteGroupedProducts();
    }
  }, [cloudSites, txaId, searchedInput]);

  useEffect(() => {
    if (version === 3) {
      setIsCollapseAll(selectedCloudSiteIds.length === cloudSites.length);
    }
  }, [selectedCloudSiteIds]);

  const handleExpandCloudsitePanel = (cloudSiteId: string) => {
    if (version === 3 && selectedCloudSiteIds.includes(cloudSiteId)) {
      setSelectedCloudSiteIds(selectedCloudSiteIds.filter((id) => id !== cloudSiteId));
      return;
    }
    setSelectedCloudSiteIds(Array.from(new Set([...selectedCloudSiteIds, cloudSiteId])));
  };

  /**
   * isAllCloudsitesProductEmpty is used to check if all the cloudsite products are empty.
   * If all the cloudsite products are empty, then we show the empty state.
   */

  const checkIsCloudSiteProductsEmpty = (node: any) => {
    const isCloudSiteProductsEmpty = isEmpty(
      cloudsiteIdAndProductsMap[node.cloudSiteId]?.groupedProductData
        ?.cloudSiteProductsAppsAndSubscriptionsForOrganization ?? {},
      cloudsiteIdAndProductsMap[node.cloudSiteId]?.groupedProductDataError
    );
    return isCloudSiteProductsEmpty;
  };

  const isAllCloudsitesProductEmpty = cloudSites.every((node: any) => {
    return checkIsCloudSiteProductsEmpty(node);
  });

  const filteredCloudSites = cloudSites.filter((node: any) => {
    return !checkIsCloudSiteProductsEmpty(node);
  });

  const getCloudSiteProductsComponent = ({ cloudSiteId = '' }: { cloudSiteId?: string }) => {
    if (selectedCloudSiteIds.includes(cloudSiteId)) {
      return (
        <ProductsTableGroupedUnderSite
          txaId={txaId}
          invoiceGroupDetails={invoiceGroupDetails}
          invoiceGroupLoading={invoiceGroupLoading}
          productsAppsAndSubscriptions={
            cloudsiteIdAndProductsMap[cloudSiteId]?.groupedProductData
              ?.cloudSiteProductsAppsAndSubscriptionsForOrganization ?? {}
          }
          groupedProductDataError={cloudsiteIdAndProductsMap[cloudSiteId]?.groupedProductDataError}
          groupedProductDataLoading={cloudsiteIdAndProductsMap[cloudSiteId]?.groupedProductDataLoading}
          isEmpty={!!cloudsiteIdAndProductsMap[cloudSiteId]?.groupedProductDataError}
          subscriptionColumnVersion={subscriptionColumnVersion}
          isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
          version={version}
          isMultiInstanceAppsEnabled={isMultiInstanceAppsEnabled}
        />
      );
    }
    return <></>;
  };

  const cloudsiteLineItemsContent = cloudSites.map((node: any, index: number) => {
    /**
     * isCloudSiteProductsEmpty is used to check if the cloudsite products are empty.
     * If the cloudsite products are empty, then we don't show the cloudsite panel.
     */
    const isCloudSiteProductsEmpty = checkIsCloudSiteProductsEmpty(node);

    const getHeader = () => {
      if (version === 3) {
        return (
          <Box paddingInlineStart="space.300">
            <div className="flex align-center gap1">
              <WorldIcon label="Cloud site" />
              {node.cloudSiteHostname}
            </div>
          </Box>
        );
      }
      return node.cloudSiteHostname;
    };

    return !isCloudSiteProductsEmpty ? (
      <CustomPanel
        testId={`cloud-site-${index}`}
        expandedHeader={getHeader()}
        collapsedHeader={getHeader()}
        onChange={() => handleExpandCloudsitePanel(node.cloudSiteId as string)}
        key={node.cloudSiteId}
        isCustomExpanded={index === 0}
        isExpanded={version === 3 && selectedCloudSiteIds.includes(node.cloudSiteId)}
        version={version}
      >
        {getCloudSiteProductsComponent({
          cloudSiteId: node.cloudSiteId,
        })}
      </CustomPanel>
    ) : (
      <></>
    );
  });

  const handleCollapseAll = () => {
    setIsCollapseAll(!isCollapseAll);
    if (isCollapseAll && selectedCloudSiteIds.length > 0) {
      setSelectedCloudSiteIds([]);
    } else {
      setSelectedCloudSiteIds(cloudSites.map((node: any) => node.cloudSiteId));
    }
  };

  if (version === 3 && (loading || isFetchAllCloudsiteProductLoading)) {
    return renderAccordionSectionLoader(true);
  }

  if (version === 3 && (cloudSites.length === 0 || isAllCloudsitesProductEmpty)) {
    if (areProductsEmpty) {
      return (
        <div className="no-data-view">
          <EmptyState {...(searchedInput ? emptyStatePropForWrongSearch : genericEmptyStateProp)} />
        </div>
      );
    }
    return null;
  }

  return (
    <div className="org-detail-sites-container">
      <div className="org-detail-sites-container-heading flex align-center justify-space-between">
        <h3>{version === 3 ? 'Sites' : 'Subscriptions under sites'}</h3>
        {version === 3 && !isAllCloudsitesProductEmpty && filteredCloudSites.length > 1 && (
          <Button appearance="subtle" onClick={handleCollapseAll}>
            <Text color="color.text.selected">{`${isCollapseAll ? 'Collapse' : 'Expand'} all`}</Text>
          </Button>
        )}
      </div>
      {loading || isFetchAllCloudsiteProductLoading ? (
        <div className="org-detail-sites-container-loader">
          {version === 3 ? renderAccordionSectionLoader() : <Spinner size="large" testId="sites-table-spinner" />}
        </div>
      ) : cloudSites.length === 0 || isAllCloudsitesProductEmpty ? (
        <div className="no-data-view">
          <EmptyState {...emptyStateProp} />
        </div>
      ) : (
        cloudsiteLineItemsContent
      )}
    </div>
  );
};

export default memo(SitesTable);
